import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import { LoaderCircle } from 'lucide-react';
import { FaTrash } from 'react-icons/fa';
import Styles from './softwareDeploymentStyle.module.scss';
import Project from '../../common/Utils/project';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';

type SoftwareEntry = {
  id: string;
  applicationName: string;
  publisher: string;
  operatingSystemType: string;
  operatingSystemVersion: string;
  filePath: string;
};
const itemsPerPage = 20;
interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const DeploymentTable: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [deploymentList, setDeploymentList] = useState<SoftwareEntry[]>([]);
  const [loading, setLoading] = useState(true); // ① explicit loader flag
  const navigate = useNavigate();
  const auth = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const { selectedTenantId } = useTenantSelection();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSoftwareId, setSelectedSoftwareId] = useState<string | null>(null);
  const { canEdit } = usePermissions();
  const canManageDeployments = canEdit('softwareDeployment');

  const getSoftwareDeploymentList = async (page = 1) => {
    try {
      if (auth && auth.userData) {
        setLoading(true);

        const token = auth.userData.access_token;
        const tenantId = selectedTenantId;

        const queryParams = new URLSearchParams({
          pageNumber: page.toString(),
          pageSize: itemsPerPage.toString(),
        });

        if (search.trim()) {
          queryParams.append('search', search.trim());
        }

        const { data } = await api.get(
          `${Project.api}softwaredeploymentservice/SoftwareDeployment/${tenantId}/software-deployment-list?${queryParams}`,
        );

        setDeploymentList(data.data ?? []);
        setPaginationInfo(data.pagination ?? null);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSoftwareDeploymentList(currentPage);
  }, [auth, currentPage, search]);

  const deleteSoftware = async (softwareId: string) => {
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;
        // console.log('tenantGroupId', tenantGroupId);
        await api.delete(`${Project.api}softwaredeploymentservice/SoftwareDeployment/${tenantId}/delete-software-deployment/${softwareId}`);

        getSoftwareDeploymentList(currentPage);
      }
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  const totalPages = paginationInfo?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  /* -------- search filter -------- */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return deploymentList;

    return deploymentList.filter((item) => {
      const fields = [
        item.applicationName || '',
        item.publisher || '',
        item.operatingSystemType || '',
        item.operatingSystemVersion || '',
        item.filePath ? item.filePath.split('/').pop() || '' : '',
      ];

      return fields.some((field) => field.toLowerCase().includes(term));
    });
  }, [deploymentList, search]);

  return (
    <div className="text-white p-6 rounded-md shadow-lg">
      <div className="flex justify-between items-center mb-4">
        <input
          className={Styles['searchInput']}
          placeholder=" Search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput); // apply search
              setCurrentPage(1); // reset page
            }
          }}
        />
        {canManageDeployments && (
          <button className={Styles['createBtn']} onClick={() => navigate('/SoftwareDeployment/PatchDeployment')}>
            Create Software
          </button>
        )}
      </div>

      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: '#1D1D1D',
              color: '#FEFEFE',
              padding: '2rem',
              borderRadius: '30px',
              width: '90%',
              maxWidth: '500px',
              textAlign: 'center',
            }}
          >
            <img src="/images/delete-icon.svg" alt="delete" style={{ marginLeft: '200px', marginBottom: '1rem' }} />
            <h5>Are you sure you want to delete?</h5>
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  background: '#1D1D1D',
                  border: '1px solid #FEFEFE',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  marginRight: '30px',
                }}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  background: '#D12929',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (selectedSoftwareId) {
                    deleteSoftware(selectedSoftwareId);
                  }
                  setShowDeleteModal(false);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto mt-10">
        <table className="w-full table-auto border-collapse" style={{ fontSize: 16 }}>
          <thead>
            <tr>
              {['Application Name', 'Publisher', 'OS Type', 'OS Version', 'File', 'Actions']?.map((h) => (
                <th key={h} className="text-left px-3 py-2 border-gray-700 normal font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* ③ loading spinner */}
            {loading ? (
              <tr>
                <td colSpan={5} className="py-12 text-center">
                  <LoaderCircle className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              /* ④ no‑results state */
              <tr>
                <td colSpan={5} className="py-12 text-center text-gray-400">
                  No matching records
                </td>
              </tr>
            ) : (
              filtered?.map((item, idx) => (
                <tr
                  key={idx}
                  style={{
                    borderBottom: '1px solid #2a2a2a',
                    background: idx % 2 === 0 ? '#161617' : '#232222',
                    height: 60,
                  }}
                >
                  <td className="px-4 py-2">{item.applicationName}</td>
                  <td className="px-4 py-2">{item.publisher}</td>
                  <td className="px-4 py-2">{item.operatingSystemType}</td>
                  <td className="px-4 py-2">{item.operatingSystemVersion}</td>
                  <td className="px-4 py-2">{item.filePath ? item.filePath.split('/').pop() : 'N/A'}</td>
                  <td className="px-4 py-2">
                    {/* <button
                      title="Edit"
                      className="p-2 bg-gray-700 rounded hover:bg-gray-600"
                      // onClick={() => navigate(`/groupPolicy/CreateGroup/${item.groupId}`)}
                    >
                      <FaEdit />
                    </button> */}
                    {canManageDeployments && (
                      <button
                        title="Delete"
                        className="p-2 bg-gray-700 rounded hover:bg-gray-600 ml-[10px]"
                        onClick={() => {
                          setSelectedSoftwareId(item.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={handlePageChange} />
    </div>
  );
};

export default DeploymentTable;
